import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { PageContainer } from "~/components/layout/PageContainer";
import { AppHeader } from "~/components/layout/AppHeader";
import { RaffleAnotherOneIsPlaying } from "~/assets/raffle/RaffleAnotherOneIsPlaying";
import { Button } from "@shadcn/ui/button";
import { usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { preloadQuery } from "@relay";
import { useAsyncMutation } from "@hooks/useAsyncMutation";
import { toast } from "sonner";
import { router } from "~/router";
import { RafflePlayPolling } from "@components/Polling/RafflePlayPolling";
import { useState } from "react";
import type { anotherOnePlayingQuery } from "./__generated__/anotherOnePlayingQuery.graphql";
import type { anotherOnePlayingEnterMutation } from "./__generated__/anotherOnePlayingEnterMutation.graphql";

const query = graphql`
	query anotherOnePlayingQuery($hubMetaName: String!) {
		hubMeta(by: { name: $hubMetaName }) @required(action: THROW) {
			id
			name
			hub @required(action: THROW) {
				id
			}
		}
	}
`;

const raffleEnterMutation = graphql`
	mutation anotherOnePlayingEnterMutation($hubId: UUID!) {
		raffleEnter(input: { hubId: $hubId }) {
			id
			status
			hub {
				id
			}
			user {
				id
			}
		}
	}
`;

export const Route = createFileRoute("/$hubMetaName/raffle/another-one-playing")({
	beforeLoad: () => {
		document.title = "bacehub | Startseite";
	},
	component: RouteComponent,
	loader: async (ctx) => {
		return preloadQuery<anotherOnePlayingQuery>(ctx.context.relayEnvironment, query, {
			hubMetaName: ctx.params.hubMetaName,
		});
	},
});

function RouteComponent() {
	const { hubMetaName } = Route.useParams();
	const navigate = useNavigate();
	const i18n = useI18nContext().LL["new-ui"];
	const { hubMeta } = usePreloadedQuery<anotherOnePlayingQuery>(query, Route.useLoaderData());
	const [enterRaffle, isEntering] = useAsyncMutation<anotherOnePlayingEnterMutation>(raffleEnterMutation);
	const [canPlay, setCanPlay] = useState(false);

	const handlePlayNow = async () => {
		if (!canPlay) {
			return;
		}
		try {
			const result = await enterRaffle({ variables: { hubId: hubMeta.hub.id } });
			await router.invalidate();
			navigate({
				to: "/$hubMetaName/raffle/$raffleId/play",
				params: { hubMetaName, raffleId: result.raffleEnter.id },
			});
		} catch (error: any) {
			// Check for specific GraphQL error code
			if (error?.errors?.[0]?.extensions?.code === "OTHER_RAFFLE_IN_PROGRESS") {
				toast.info(i18n.RAFFLE_ANOTHER_PLAYING_ERROR());
			} else if (
				error?.errors?.[0]?.extensions?.code === "PRECONDITION_FAILED" &&
				error?.errors?.[0]?.extensions?.details === "No prize in Hub"
			) {
				toast.info(i18n.RAFFLE_REFILLING_PRIZE());
			} else {
				toast.error(i18n.RAFFLE_ENTER_FAILED_ERROR());
			}
		}
	};

	return (
		<PageContainer
			footer={
				<Button className={`w-full shadow-xl ${!canPlay ? "opacity-50" : ""}`} size="lg" disabled={isEntering} onClick={handlePlayNow}>
					<T>{i18n.RAFFLE_PLAY_BUTTON()}</T>
				</Button>
			}
		>
			<AppHeader service="RAFFLE" />
			<RafflePlayPolling.ContinuousCheck
				hubId={hubMeta.hub.id}
				onUpdate={(canJoin) => {
					setCanPlay(canJoin);
				}}
			/>
			<div className="flex flex-col items-center gap-4 px-6">
				<div className="flex w-full max-w-md flex-col gap-2 py-4 text-left">
					<h1 className="text-2xl text-foreground">
						<T>{i18n.RAFFLE_ANOTHER_PLAYING_ERROR()}</T>
					</h1>
				</div>
				<div className="flex h-64 w-full max-w-md items-center justify-center">
					<RaffleAnotherOneIsPlaying className="h-48 w-auto" />
				</div>
			</div>
		</PageContainer>
	);
}
